import React, { useState, useMemo } from "react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import flags from "react-phone-number-input/flags";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

function getCountryName(country: CountryCode): string {
  try {
    return displayNames.of(country) ?? country;
  } catch {
    return country;
  }
}

const COUNTRY_OPTIONS = getCountries().map((country) => ({
  country: country as CountryCode,
  code: getCountryCallingCode(country),
  name: getCountryName(country as CountryCode),
}));

export interface NationalityCountryInputProps {
  /** ISO 3166-1 alpha-2 country code (e.g. CH, US). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  id?: string;
}

export function NationalityCountryInput({
  value,
  onChange,
  placeholder = "Select country",
  hasError,
  className,
  id,
}: NationalityCountryInputProps) {
  const [open, setOpen] = useState(false);

  const selectedCountry = (value?.trim().toUpperCase().slice(0, 2) || "") as CountryCode;
  const isValidCode = selectedCountry.length === 2 && COUNTRY_OPTIONS.some((o) => o.country === selectedCountry);
  const displayCountry = isValidCode ? selectedCountry : null;
  const currentOption = useMemo(
    () => COUNTRY_OPTIONS.find((o) => o.country === displayCountry),
    [displayCountry]
  );
  const FlagComponent = displayCountry ? flags[displayCountry] : null;

  const handleSelect = (country: CountryCode) => {
    setOpen(false);
    onChange(country);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          className={cn(
            "flex w-full items-center gap-2 rounded-xl border bg-white px-3 py-2 text-left text-sm text-neutral-900 transition-colors outline-none focus:ring-2 focus:ring-alpine-gold/30 focus:ring-inset",
            hasError ? "border-red-500 bg-red-50/50" : "border-gray-200",
            !currentOption && "text-neutral-900/50",
            className
          )}
          aria-label="Nationality"
        >
          {FlagComponent && currentOption ? (
            <>
              <span className="w-5 h-[0.75rem] overflow-hidden rounded-sm flex shrink-0 items-center justify-center">
                {React.createElement(FlagComponent as React.ComponentType<{ title?: string }>, {
                  title: currentOption.name,
                })}
              </span>
              <span className="flex-1 min-w-0 truncate font-medium">{currentOption.name}</span>
            </>
          ) : (
            <span className="flex-1 min-w-0 truncate">{placeholder}</span>
          )}
          <ChevronDown className={cn("h-4 w-4 shrink-0 opacity-60 transition-transform", open && "rotate-180")} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRY_OPTIONS.map((opt) => {
                const FlagItem = flags[opt.country];
                return (
                  <CommandItem
                    key={opt.country}
                    value={`${opt.name} ${opt.country} +${opt.code}`}
                    onSelect={() => handleSelect(opt.country)}
                    className="flex items-center gap-2 py-2 rounded-sm hover:bg-pine-green/[0.06] data-[selected=true]:bg-pine-green/[0.08]"
                  >
                    {FlagItem && (
                      <span className="w-5 h-[0.75rem] overflow-hidden rounded-sm shrink-0 flex items-center justify-center">
                        {React.createElement(FlagItem as React.ComponentType<{ title?: string }>, {
                          title: opt.name,
                        })}
                      </span>
                    )}
                    <span className="text-sm text-neutral-900 font-medium">{opt.name}</span>
                    <span className="text-xs text-neutral-900/60">({opt.country})</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
